import {announce} from '@github-ui/aria-live'
import {useAnalytics} from '@github-ui/use-analytics'
import {CheckIcon, SparkleIcon, XIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {useEffect, useEffectEvent} from 'react'

import styles from './AllowedValuesSuggestion.module.css'

export interface AllowedValuesSuggestionProps {
  suggestedValues: string[]
  onAccept: () => void
  onDismiss: () => void
  className?: string
}

export function AllowedValuesSuggestion({
  suggestedValues,
  onAccept,
  onDismiss,
  className,
}: AllowedValuesSuggestionProps) {
  const {sendAnalyticsEvent} = useAnalytics()

  const noSuggestions = suggestedValues.length === 0

  const announceSuggestion = useEffectEvent(() => {
    if (noSuggestions) return
    announce(`${suggestedValues.length} suggested ${suggestedValues.length === 1 ? 'option' : 'options'}.`)
  })

  const sendShownAnalyticsEvent = useEffectEvent(() => {
    if (noSuggestions) return
    sendAnalyticsEvent('repo_custom_properties_suggestions.allowed_values.shown')
  })

  useEffect(() => {
    announceSuggestion()
    sendShownAnalyticsEvent()
  }, [])

  if (noSuggestions) {
    return null
  }

  return (
    <div
      data-testid="allowed-values-suggestion"
      className={clsx('d-flex flex-items-center gap-3 pl-3 pr-2 py-1 color-bg-subtle border-top', className)}
    >
      <SparkleIcon size="small" className="color-fg-muted ml-1" />

      <PreviewValues suggestedValues={suggestedValues} />

      <div className={clsx('d-flex flex-items-center ml-auto', styles.SmallMarginRight)}>
        <div className="d-flex gap-1">
          <IconButton
            size="small"
            variant="invisible"
            icon={CheckIcon}
            aria-label="Accept suggestion"
            onClick={() => {
              sendAnalyticsEvent('repo_custom_properties_suggestions.allowed_values.clicked')
              onAccept()
            }}
          />
          <IconButton
            size="small"
            variant="invisible"
            icon={XIcon}
            aria-label="Dismiss suggestion"
            onClick={onDismiss}
          />
        </div>
      </div>
    </div>
  )
}

function PreviewValues({suggestedValues}: {suggestedValues: string[]}) {
  const [firstValue, secondValue] = suggestedValues
  const totalValues = suggestedValues.length

  return (
    <>
      {/* Display two preview values on wider viewports */}
      <div className={clsx('flex-items-center gap-2 ml-1 min-width-0', styles.HideOnSmallScreen)}>
        <strong className={styles.Truncate}>
          {firstValue}
          {secondValue && <span>, {secondValue}</span>}
        </strong>
        {totalValues > 2 && <span className="color-fg-muted no-wrap"> + {totalValues - 2} more</span>}
      </div>

      {/* Display one preview value on narrow viewports */}
      <div className={clsx('flex-items-center gap-2 ml-1 min-width-0', styles.SmallScreenOnly)}>
        <strong className={styles.Truncate}>{firstValue}</strong>
        {totalValues > 1 && <span className="color-fg-muted no-wrap"> + {totalValues - 1} more</span>}
      </div>
    </>
  )
}
